import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, HttpError } from "../../lib/api-client.js";
import { OPEN, toAuthView, type AuthView, type MeData } from "./auth-state.js";

const ME_KEY = ["auth", "me"] as const;

export interface AuthState extends AuthView {
  isLoading: boolean;
}

export function useAuth(): AuthState {
  const q = useQuery({
    queryKey: ME_KEY,
    queryFn: async (): Promise<MeData> => {
      try {
        const res = await api.authMe();
        return res.user ?? OPEN;
      } catch (err) {
        if (err instanceof HttpError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 30_000,
    retry: false,
  });
  return { isLoading: q.isLoading, ...toAuthView(q.data ?? null) };
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    // Scoped `auth` so App.tsx's mutationCache 401 handler skips it — a
    // rejected login is a 401 by design, not an expired session.
    mutationKey: ["auth", "login"],
    mutationFn: async (creds: { email: string; password: string }) => {
      const res = await api.authLogin(creds.email, creds.password);
      return res.user;
    },
    onSuccess: (user) => {
      qc.setQueryData(ME_KEY, user);
      // Refetch everything else now that we have access.
      void qc.invalidateQueries();
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ["auth", "logout"],
    mutationFn: () => api.authLogout(),
    onSuccess: () => {
      qc.setQueryData(ME_KEY, null);
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== "auth" });
    },
  });
}
